import { motion } from "framer-motion";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { ScrollColorHeading } from "@/components/ScrollColorHeading";
import { SectionHeading, SectionLabel } from "@/components/SectionHeading";
import { CTASection } from "@/components/CTASection";
import { Stats } from "@/components/Stats";
import { VALUES, STATS, DIFFERENTIATORS } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const IMG_STORY = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_TEAM = "https://images.unsplash.com/photo-1688380692117-63178554d76d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Split = ({ label, title, children, image, reverse = false }) => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
    <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
      <SectionLabel dark>{label}</SectionLabel>
      <ScrollColorHeading as="h3" text={title} className="mt-5 font-heading font-extrabold text-3xl md:text-5xl leading-tight tracking-tight" />
      <div className="mt-6 space-y-4 text-brand-plum/70 text-lg leading-relaxed">{children}</div>
    </div>
    <Reveal className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
      <div className="overflow-hidden rounded-2xl aspect-[4/3]">
        <img src={image} alt={title} loading="lazy" className="h-full w-full object-cover" />
      </div>
    </Reveal>
  </div>
);

export default function About() {
  useSEO({
    title: "About | Tangensis Business Solutions",
    description: "Tangensis is a strategic business and digital solutions partner helping ambitious organizations improve, innovate and grow across strategy, technology, data and enterprise platforms.",
    path: "/about",
  });
  return (
    <>
      <PageHero
        label="About Tangensis"
        titleLines={["STRATEGY WITH", "PURPOSE.", "SOLUTIONS WITH", "IMPACT."]}
        intro="We are a strategic business and digital solutions partner built to help ambitious organizations improve, innovate and grow."
      />

      <section className="bg-brand-offwhite">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-24 space-y-28 md:space-y-40">
          <Split label="Our Story" title="Born to turn complexity into clarity." image={IMG_STORY}>
            <p>Tangensis Business Solutions was founded on a simple belief: that great strategy and practical execution should never live apart. Too often, organizations are handed bold recommendations with no real path to deliver them — so we built a firm that does both, side by side.</p>
            <p>From digital transformation and growth strategy to technology consulting and ERP, we partner with organizations across the USA, UK, UAE, Australia, India, Canada, Malaysia and Singapore — turning complex challenges into clear, measurable progress.</p>
          </Split>
          <Split label="Who We Are" title="A partner, not just a provider." image={IMG_TEAM} reverse>
            <p>We are strategists, consultants and problem-solvers who care as much about outcomes as our clients do.</p>
            <p>Our team blends business insight with hands-on technical depth — spanning strategy, digital, data, cloud and enterprise platforms. We embed with your people, share ownership of the outcome, and stay focused on impact long after the recommendations are made.</p>
          </Split>
        </div>
      </section>

      <section className="relative bg-white noise-overlay">
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-14">
          <Reveal>
            <div className="border-t border-brand-ink/15 pt-8">
              <SectionLabel>Our Mission</SectionLabel>
              <p className="mt-6 font-heading font-bold text-brand-ink text-2xl md:text-3xl leading-snug">
                To transform business challenges into strategic opportunities through innovative, practical and results-driven solutions.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border-t border-brand-ink/15 pt-8">
              <SectionLabel>Our Vision</SectionLabel>
              <p className="mt-6 font-heading font-bold text-brand-ink text-2xl md:text-3xl leading-snug">
                To be the partner ambitious organizations trust to help them grow, transform and lead their markets.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-offwhite">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-24">
          <SectionHeading dark label="Our Values" title="The principles behind every engagement." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 5) * 0.08}>
                <div className="h-full bg-white rounded-2xl p-7 border border-brand-plum/10 hover:-translate-y-1 transition-transform duration-300">
                  <span className="font-display font-black text-2xl gradient-text">{String(i + 1).padStart(2, "0")}</span>
                  <h4 className="mt-4 font-heading font-bold text-xl text-brand-ink">{v.title}</h4>
                  <p className="mt-2 text-brand-plum/60 leading-relaxed text-sm">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-white noise-overlay">
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-24">
          <SectionHeading label="Why clients choose us" title="Reasons partners stay with Tangensis." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
            {DIFFERENTIATORS.map((d, i) => (
              <Reveal key={d.num} delay={(i % 3) * 0.1}>
                <div className="border-t border-brand-ink/15 pt-6">
                  <span className="font-display font-black text-4xl gradient-text">{d.num}</span>
                  <h3 className="mt-4 font-heading font-bold text-2xl text-brand-ink">{d.title}</h3>
                  <p className="mt-3 text-brand-ink/75 leading-relaxed">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Stats stats={STATS} />
      <CTASection heading={["LET'S BUILD SOMETHING", "THAT LASTS."]} />
    </>
  );
}
